/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/ScheduleProposal.tsx
 * PURPOSE: "Đăng ký ca làm việc" (nhân viên, WEB) — chọn mã ca cho từng ngày
 *          trong tuần từ đúng danh mục quản lý đang dùng, gửi lên chờ duyệt.
 *          Song song với màn "Lịch làm việc" (lịch chính thức), KHÔNG thay thế.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: WFM — nhân viên tự đăng ký ca, quản lý duyệt trên lưới SC-009 [2d]
 * - Impact: Trước 2026-09-04 web KHÔNG có luồng này (chỉ mobile) — nhân viên
 *   dùng web không đăng ký được ca, và cũng không thấy đề xuất mình đã gửi
 *   (`/schedules/my-schedule` lọc bỏ ca EMPLOYEE chưa duyệt — đo thật: đề xuất
 *   PENDING ⇒ màn Lịch web trả 0 ca).
 * - Stakeholders: Nhân viên ca, Quản lý chi nhánh
 *
 * @technical-architecture
 * - Layer: Presentation (route /schedule/register)
 * - Pattern: app-shell 480px + TanStack Query, mirror 1:1 nghiệp vụ bản mobile
 * - Dependencies: features/scheduleProposal/useScheduleProposal
 *
 * @cross-references
 * - Backend: backend/app/api/v2/endpoints/schedule_proposal.py (3 endpoint)
 * - Bản mobile (nguồn nghiệp vụ): mobile/src/screens/schedule/ScheduleProposalScreen.tsx
 * - Duyệt: SC-009-SCHEDULE-GRID §ANNOTATION [2d]/[EXP] trong
 *   docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 * - Lối vào: frontend/src/pages/ScheduleModern.tsx (nút "Đăng ký ca")
 *
 * @note Quy tắc nghiệp vụ chép từ bản mobile, KHÔNG tự phát minh:
 *   (1) "Ca nghỉ" chọn được MỌI ngày trong tuần — ngày nghỉ tuần theo lịch
 *       từng người (ECO-DEC-2026-144 W1; đã bỏ luật cứng "chỉ T2–T5" của A4).
 *   (2) Ngày đã có ca CHÍNH THỨC (quản lý xếp / đề xuất đã duyệt) ⇒ chỉ đọc.
 *   (3) Ca `cancelled` KHÔNG tính là ca chính thức (nếu tính, một ca đã huỷ sẽ
 *       khoá mất ngày đó — lỗi mobile đã gặp và đã sửa).
 *   (4) Gửi = THAY THẾ toàn bộ đề xuất PENDING/REJECTED của tuần ⇒ payload
 *       phải gồm mọi ngày chưa duyệt, kể cả ngày bỏ trống (null).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { EmployeeBottomNav } from '@/components/navigation/EmployeeBottomNav';
import {
  useMyWeekProposal,
  useOfficialWeekSchedule,
  useProposalShiftCodes,
  useSubmitWeekProposal,
} from '@/features/scheduleProposal/useScheduleProposal';
import type { ProposalDayInput, ProposalShiftItem } from '@/services/api/scheduleProposalApi';
import styles from './ScheduleProposal.module.css';

const DAY_LABELS_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
/** "Không đăng ký ca" — giá trị riêng của <select>, KHÔNG gửi lên máy chủ. */
const NONE_OPTION_VALUE = '__NONE__';

const pad2 = (n: number) => String(n).padStart(2, '0');
/** Ngày local → "YYYY-MM-DD" (không đi qua UTC — lệch múi giờ đổi mất ngày). */
const toLocalISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const shortVN = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
/** "08:00:00" | "08:00" → "08:00" */
const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : '');

function startOfWeekMonday(d: Date): Date {
  const dow = d.getDay(); // 0=CN
  const diff = dow === 0 ? -6 : 1 - dow;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
}
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const ScheduleProposal: React.FC = () => {
  const navigate = useNavigate();
  const [weekStartDate, setWeekStartDate] = useState<Date>(() => startOfWeekMonday(new Date()));

  const weekStartIso = useMemo(() => toLocalISO(weekStartDate), [weekStartDate]);
  const weekEndDate = useMemo(() => addDays(weekStartDate, 6), [weekStartDate]);
  const weekEndIso = useMemo(() => toLocalISO(weekEndDate), [weekEndDate]);
  // bot-037 (GH#803): tuần đã khoá — cùng ngưỡng "sunday < today" phía backend
  // (manager_schedule.py is_past_week/publish_week: `sunday < vn_today()`),
  // giờ LOCAL trình duyệt + so sánh chuỗi ISO ngày (không giờ), khớp convention
  // "hôm nay" của ManagerSchedulePage.tsx (vd `rangeToISO < todayISO`).
  const isLockedWeek = weekEndIso < toLocalISO(new Date());

  const shiftCodesQuery = useProposalShiftCodes();
  const weekProposalQuery = useMyWeekProposal(weekStartIso);
  const officialQuery = useOfficialWeekSchedule(weekStartIso, weekEndIso);
  const submitMutation = useSubmitWeekProposal();

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const date = addDays(weekStartDate, i);
      return { date, iso: toLocalISO(date), label: DAY_LABELS_SHORT[date.getDay()] };
    }),
    [weekStartDate]
  );

  const [selections, setSelections] = useState<Record<string, string | null>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Nạp lại lựa chọn đang có (PENDING/REJECTED) khi đổi tuần hoặc dữ liệu về.
  useEffect(() => {
    setSubmitted(false);
    setSubmitError(null);
    const items = weekProposalQuery.data?.items ?? [];
    const codes = shiftCodesQuery.data?.items ?? [];
    const next: Record<string, string | null> = {};
    for (const item of items) {
      if (item.approval_status === 'APPROVED') continue; // đã duyệt — không sửa ở đây
      const match = codes.find(
        c =>
          (item.shift_code != null && c.code === item.shift_code) ||
          (hhmm(c.start_time) === hhmm(item.start_time) && hhmm(c.end_time) === hhmm(item.end_time))
      );
      if (match) next[item.date] = match.id;
    }
    setSelections(next);
  }, [weekStartIso, weekProposalQuery.data, shiftCodesQuery.data]);

  const proposalByDate = useMemo(() => {
    const map = new Map<string, ProposalShiftItem>();
    for (const item of weekProposalQuery.data?.items ?? []) map.set(item.date, item);
    return map;
  }, [weekProposalQuery.data]);

  const officialByDate = useMemo(() => {
    const map = new Map<string, NonNullable<typeof officialQuery.data>[number]>();
    for (const s of officialQuery.data ?? []) map.set(s.date, s);
    return map;
  }, [officialQuery.data]);

  const dayOffTemplate = useMemo(
    () => (shiftCodesQuery.data?.items ?? []).find(c => c.is_day_off),
    [shiftCodesQuery.data]
  );

  const optionsForDay = () => {
    const codes = (shiftCodesQuery.data?.items ?? []).filter(c => !c.is_day_off);
    const opts: Array<{ value: string; label: string }> = [
      { value: NONE_OPTION_VALUE, label: 'Không đăng ký ca (bỏ trống)' },
    ];
    if (dayOffTemplate) {
      opts.push({ value: dayOffTemplate.id, label: `${dayOffTemplate.code ?? 'NGHI'} · Ca nghỉ` });
    }
    for (const c of codes) {
      opts.push({
        value: c.id,
        label: c.code
          ? `${c.code} · ${c.name} ${hhmm(c.start_time)}–${hhmm(c.end_time)}`
          : `${c.name} ${hhmm(c.start_time)}–${hhmm(c.end_time)}`,
      });
    }
    return opts;
  };

  const handleSubmit = () => {
    // bot-037 (GH#803): chặn gửi đề xuất cho tuần đã khoá — trước đây không
    // có chốt nào, ghi thẳng giờ vào tuần quá khứ.
    if (isLockedWeek) return;
    setSubmitError(null);
    setSubmitted(false);
    const payload: ProposalDayInput[] = days
      .filter(d => proposalByDate.get(d.iso)?.approval_status !== 'APPROVED' && !officialByDate.get(d.iso))
      .map(d => ({ date: d.iso, shift_template_id: selections[d.iso] ?? null }));
    submitMutation.mutate(
      { week_start: weekStartIso, days: payload },
      {
        onSuccess: () => setSubmitted(true),
        onError: (err: unknown) => {
          const anyErr = err as { response?: { data?: { detail?: string } }; message?: string };
          setSubmitError(
            anyErr?.response?.data?.detail ||
              anyErr?.message ||
              'Gửi đề xuất không thành công. Vui lòng thử lại.'
          );
        },
      }
    );
  };

  const loading = shiftCodesQuery.isLoading || weekProposalQuery.isLoading;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button
            type="button"
            onClick={() => navigate('/schedule')}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="schedule-proposal-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <h1 id="schedule-proposal-title" className={styles.title}>Đăng ký ca làm việc</h1>
        </div>
      </header>

      <main aria-labelledby="schedule-proposal-title" className={styles.main}>
        {/* Điều hướng tuần */}
        <div className={styles.weekNav}>
          <button
            type="button"
            className={styles.weekNavButton}
            onClick={() => setWeekStartDate(prev => addDays(prev, -7))}
            aria-label="Tuần trước"
            data-testid="schedule-proposal-prev-week"
          >
            ←
          </button>
          <span className={styles.weekLabel} data-testid="schedule-proposal-week-label">
            {shortVN(weekStartDate)} – {shortVN(weekEndDate)}
          </span>
          <button
            type="button"
            className={styles.weekNavButton}
            onClick={() => setWeekStartDate(prev => addDays(prev, 7))}
            aria-label="Tuần sau"
            data-testid="schedule-proposal-next-week"
          >
            →
          </button>
        </div>

        {isLockedWeek && (
          <div className={`${styles.banner} ${styles.bannerError}`} role="status" data-testid="schedule-proposal-locked-week">
            🔒 Tuần đã qua — chỉ xem, không đăng ký được nữa.
          </div>
        )}
        {submitted && (
          <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status" data-testid="schedule-proposal-success">
            Đã gửi đề xuất tuần {shortVN(weekStartDate)} – {shortVN(weekEndDate)} — chờ quản lý duyệt.
          </div>
        )}
        {submitError && (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert" data-testid="schedule-proposal-error">
            {submitError}
          </div>
        )}
        {shiftCodesQuery.isError && (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            Không tải được danh mục mã ca.
          </div>
        )}

        {loading ? (
          <div className={styles.card} role="status" aria-live="polite">
            <p className={styles.mutedText}>Đang tải danh mục ca và đề xuất của bạn…</p>
          </div>
        ) : (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Chọn ca cho từng ngày</h2>
            <div className={styles.dayList}>
              {days.map(d => {
                const existing = proposalByDate.get(d.iso);
                const official = officialByDate.get(d.iso);
                const isApproved = existing?.approval_status === 'APPROVED' || !!official;
                return (
                  <div key={d.iso} className={styles.dayRow} data-testid={`schedule-proposal-day-${d.iso}`}>
                    <div className={styles.dayHead}>
                      <span className={styles.dayLabel}>{d.label} {shortVN(d.date)}</span>
                      {existing?.approval_status === 'PENDING' && (
                        <span className={`${styles.tag} ${styles.tagPending}`}>Chờ duyệt</span>
                      )}
                      {isApproved && <span className={`${styles.tag} ${styles.tagApproved}`}>Đã duyệt</span>}
                      {existing?.approval_status === 'REJECTED' && (
                        <span className={`${styles.tag} ${styles.tagRejected}`}>Bị từ chối</span>
                      )}
                    </div>

                    {existing?.approval_status === 'REJECTED' && existing.rejection_reason && (
                      <p className={styles.rejectReason} data-testid={`schedule-proposal-reject-reason-${d.iso}`}>
                        Lý do: {existing.rejection_reason}
                      </p>
                    )}

                    {isApproved ? (
                      <p className={styles.approvedInfo}>
                        {official
                          ? `${official.shift_code ?? 'Ca'} · ${hhmm(official.start_time)}–${hhmm(official.end_time)} — đã có trên Lịch, liên hệ quản lý nếu cần đổi.`
                          : existing?.is_day_off
                            ? 'Ca nghỉ — đã duyệt, liên hệ quản lý nếu cần đổi.'
                            : `${existing?.shift_code ?? existing?.shift_label} · ${hhmm(existing?.start_time)}–${hhmm(existing?.end_time)} — đã duyệt, liên hệ quản lý nếu cần đổi.`}
                      </p>
                    ) : (
                      <>
                        <select
                          className={styles.select}
                          value={selections[d.iso] ?? NONE_OPTION_VALUE}
                          data-testid={`schedule-proposal-select-${d.iso}`}
                          disabled={isLockedWeek}
                          onChange={e => {
                            const v = e.target.value;
                            setSelections(prev => ({ ...prev, [d.iso]: v === NONE_OPTION_VALUE ? null : v }));
                            setSubmitted(false);
                          }}
                        >
                          {optionsForDay().map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={submitMutation.isPending || loading || isLockedWeek}
          data-testid="schedule-proposal-submit"
        >
          {submitMutation.isPending ? 'Đang gửi…' : submitted ? 'Đã gửi đề xuất' : 'Gửi đề xuất tuần này'}
        </button>
        <p className={styles.footNote}>
          Đề xuất được gửi tới quản lý chi nhánh. Ca chỉ lên Lịch làm việc sau khi được duyệt.
        </p>
      </main>

      <EmployeeBottomNav />
    </div>
  );
};

export default ScheduleProposal;
